import React from 'react';
import SectionHeader from './SectionHeader';
import Button from './Button';

/**
 * Cabecera de sección con acciones, en las diez pantallas que ya la usaban:
 * Cajas, Almacén, Tiendas, Superusuarios, Rentabilidad, Ops, Movimientos,
 * Perfil, Genética y Propietarios.
 *
 * Ahora es una capa fina sobre `SectionHeader` y `Button`. Antes se pintaba
 * sola, y sobre todo OFRECÍA UNA PALETA como prop:
 *
 *     purple · blue · red · green · emerald · slate
 *
 * Ahí estaba el problema de raíz. No es que una pantalla se equivocara de
 * color: es que el componente compartido invitaba a elegir uno, y cada sección
 * eligió el suyo. De ahí el «Nueva caja» verde y el «Cerrar caja» naranja
 * conviviendo con un sistema cuyo primario es slate.
 *
 * `color` sigue aceptándose para no romper a los diez llamantes de golpe, pero
 * ya no pinta color: se traduce a la intención del sistema. Rojo es lo único
 * que sobrevive como tal, porque «destructivo» sí es una intención y no una
 * preferencia. Lo demás es primario.
 */
/**
 * Del color a la intención.
 *
 * `red` es lo único que sobrevive tal cual, porque «destructivo» SÍ es una
 * intención. El resto —azul, verde, esmeralda, morado, pizarra— significaban
 * todos lo mismo, «esta es una acción de la barra», con cinco pinturas.
 *
 * Y como el sistema tiene un solo primario, hace falta decidir CUÁL de los
 * botones lo lleva: pintarlos todos de slate es cambiar seis colores por seis
 * botones idénticos, que no es unificar, es aplanar. Manda el primero.
 *
 * Se sostiene mirando las nueve barras: siete tienen una sola acción y es de
 * crear —«Nueva caja», «Crear Tienda», «Crear Trabajador»—. En las dos que
 * tienen dos, «Exportar Excel» va delante de «Recargar», y exportar es la
 * decisión mientras que recargar es utilidad. En Almacén, «Nuevo producto» va
 * delante de subir un ZIP y de gestionar categorías.
 *
 * Cuando no se sostenga, el llamante pasa `variant` y manda él.
 *
 * Exportada a propósito: los bloques de móvil de Almacén, Personal,
 * Superusuarios… pintan `<button>` a mano (necesitan 44px de alto y ancho
 * completo) pero deben decidir el MISMO color que aquí — un dato, una fuente.
 */
export const primarioDelPrimero = (button, index) => {
  if (button.variant) return button.variant;
  if (button.color === 'red') return 'destructive';
  return index === 0 ? 'primary' : 'outline';
};

const ButtonBar = ({ title, count, buttons = [], viewToggle, extraContent }) => (
  <SectionHeader title={title} count={count} className="mb-4">
    {viewToggle}
    <div className="flex flex-wrap items-center gap-2">
      {buttons.map((button, index) => (
        <Button
          key={index}
          variant={primarioDelPrimero(button, index)}
          onClick={button.onClick}
          disabled={button.disabled}
          title={button.title}
        >
          {button.label}
        </Button>
      ))}
      {extraContent}
    </div>
  </SectionHeader>
);

export default ButtonBar;
